import React from 'react';
import SectionCard from './SectionCard';
import { useAppContext } from '../context/AppContext';
import { sumGrants } from '../utils/runGrant';

const VARIANT_LABELS = {
  mattergen_base: 'Unconditional',
  mp_20_base: 'MP-20 Base',
  dft_band_gap: 'Band Gap (DFT)',
  ml_bulk_modulus: 'Bulk Modulus (ML)',
  chemical_system: 'Chemical System',
  chemical_system_energy_above_hull: 'Chemical System + E-above-hull',
  dft_mag_density: 'Mag. Density (DFT)',
  dft_mag_density_hhi_score: 'Mag. Density + HHI',
  space_group: 'Space Group',
};

function formatProps(props) {
  if (!props || typeof props !== 'object') return null;
  return Object.entries(props).map(([k, v]) => {
    const label = k.replace(/_/g, ' ');
    const val = typeof v === 'number' ? v.toFixed(2) : String(v);
    return `${label}: ${val}`;
  });
}

/**
 * Group MatterGen runs by the row they ran (Part 1a, contract 1.7), merging the row's jobs into
 * one entry. A run recorded before 1a (legacy toolResults, or rebuilt by loadRunCandidates)
 * has no rowId and groups by the checkpoint its body named (pretrained_name, else the
 * model_path basename), as before. A group's `name` is the row's checkpoint (`r.target`,
 * else that basename) and its `label` adds " (custom)" for a custom row, so two rows on one
 * checkpoint, or two custom models, stay two lines with their own base.
 * Spec 0.7: a row runs as several jobs, so every job that produced an extxyz is kept in
 * `jobs` (with its own per-crystal CIF and trajectory files) and `extxyzUrls`, and View
 * Crystals shows all of them. `extxyzUrl` stays the first job's file.
 */
function groupMgRuns(runs) {
  const groups = {};
  for (const r of runs) {
    const basename = r.request?.pretrained_name || (r.request?.model_path ? r.request.model_path.split('/').pop() : null) || 'unknown';
    const rowId = typeof r.rowId === 'string' && r.rowId ? r.rowId : null;
    const key = rowId ? `row:${rowId}` : `legacy:${basename}`;
    if (!groups[key]) {
      const name = typeof r.target === 'string' && r.target ? r.target : basename;
      const customModelId = typeof r.customModelId === 'string' && r.customModelId ? r.customModelId : null;
      const label = `${VARIANT_LABELS[name] || name || 'Unknown'}${customModelId ? ' (custom)' : ''}`;
      groups[key] = { key, rowId, name, customModelId, label, base: null, runs: [], totalCandidates: 0, variantTotal: null, chunkSize: null, properties: null, cifs: [], trajectories: [], trajUrl: null, runIds: [], extxyzUrl: null, extxyzUrls: [], jobs: [] };
    }
    const g = groups[key];
    g.runs.push(r);
    if (r.base && !g.base) g.base = r.base;   // a group is one row = one base
    // 0.3: a partially granted job ran `granted` candidates (MattergenRunner records it on the
    // run, Task 07.8); a run without it (an older service, a recorded run) counts its batch product.
    const jobCandidates = r.granted ?? ((r.request?.batch_size || 0) * (r.request?.num_batches || 1));
    g.totalCandidates += jobCandidates;
    // Stamped by MattergenRunner on every run (spec 0.7): the variant's requested total and
    // the chunk size it ran with, which Refine restores. Null on runs recorded before that.
    if (g.variantTotal === null && Number(r.variant_total) > 0) g.variantTotal = Number(r.variant_total);
    if (g.chunkSize === null && Number(r.chunk_size) > 0) g.chunkSize = Number(r.chunk_size);
    if (!g.properties && r.request?.properties && Object.keys(r.request.properties).length > 0) g.properties = r.request.properties;
    if (r.response?.run_id) g.runIds.push(r.response.run_id);
    if (r.response?.files?.extxyz && !g.extxyzUrl) g.extxyzUrl = r.response.files.extxyz;
    if (r.response?.files?.extxyz) {
      g.extxyzUrls.push(r.response.files.extxyz);
      g.jobs.push({
        runId: r.response.run_id || null,
        extxyz: r.response.files.extxyz,
        candidates: jobCandidates,
        cifs: Array.isArray(r.response.files.cifs) ? r.response.files.cifs : [],
        trajectories: Array.isArray(r.response.files.trajectories) ? r.response.files.trajectories : [],
      });
    }
    if (Array.isArray(r.response?.files?.cifs)) g.cifs.push(...r.response.files.cifs);
    if (Array.isArray(r.response?.files?.trajectories)) g.trajectories.push(...r.response.files.trajectories);
    if (r.response?.files?.trajectories_zip && !g.trajUrl) g.trajUrl = r.response.files.trajectories_zip;
  }
  return Object.values(groups);
}

/**
 * "capped at N" for a results row (spec 0.3): shown when the plan's per-run candidate limit
 * granted these runs fewer candidates than they requested. Runs that report no grant (an
 * older service, a recorded run) show nothing.
 */
function CappedNote({ runs }) {
  const { requested, granted } = sumGrants(runs);
  if (granted === undefined || granted >= requested) return null;
  return (
    <div style={{ fontSize: 11, color: '#f59e0b' }} title={`The plan's per-run candidate limit granted ${granted} of the ${requested} requested.`}>
      {`capped at ${granted}`}
    </div>
  );
}

/**
 * Part 1a (1.7): one line per row that did not run, shown at the top of Results. runRows
 * records every row of a run in toolResults.rowStatus with its run label (rowLabel, plus
 * " (<row id>)" when two rows of the run share one), so two failed MatterGen rows read apart
 * even though MatterGen's own errors never name the checkpoint. Only 'error' and 'rejected'
 * rows get a line. A stored entry without a usable label or message (run docs are
 * member-writable) falls back to the row id and a plain sentence, never "undefined".
 */
function rowProblemLines(rowStatus) {
  if (!rowStatus || typeof rowStatus !== 'object' || Array.isArray(rowStatus)) return [];
  const lines = [];
  for (const [rowId, entry] of Object.entries(rowStatus)) {
    if (!entry || (entry.status !== 'error' && entry.status !== 'rejected')) continue;
    const label = typeof entry.label === 'string' && entry.label ? entry.label : rowId;
    const fallback = entry.status === 'rejected' ? 'It was not run.' : 'It failed.';
    const message = typeof entry.message === 'string' && entry.message ? entry.message : fallback;
    lines.push({ rowId, status: entry.status, text: `${label}: ${message}` });
  }
  return lines;
}

/**
 * Part 1a (1.7): an FM4M featurization chunk that ran a fallback model says so.
 * FM4MRunner tries the row's model, then Mordred, then MorganFingerprint, chunk by chunk,
 * and records the row's model (requestedModel) next to the one that ran (request.model).
 * Runs recorded before 1a carry no requestedModel and show nothing.
 */
function FallbackNote({ run }) {
  const requested = run?.requestedModel;
  const ran = run?.request?.model;
  if (typeof requested !== 'string' || !requested || typeof ran !== 'string' || !ran || ran === requested) return null;
  return (
    <div style={{ fontSize: 11, color: '#f59e0b' }}>{`Fallback: ${requested} failed, so ${ran} ran instead.`}</div>
  );
}

function ResultsTable({ toolResults, plan, mgBaseUrl, ibmBaseUrl, toAbs, onRefineMattergen, onRefineFM4M, onViewPlan, onOpenFile, onPreviewSelection }) {
  const ctx = useAppContext() || {};
  const mg = mgBaseUrl || ctx.mgBaseUrl;
  const ibm = ibmBaseUrl || ctx.ibmBaseUrl;
  const abs = toAbs || ctx.toAbs || ((b, p) => p);
  const handleRefineMG = onRefineMattergen || ctx.refineMattergen;
  const handleRefineFM = onRefineFM4M || ctx.refineFM4M;
  const tr = toolResults || ctx.toolResults || {};
  const runTs = ctx.toolResultsTs || null;
  const pl = plan || ctx.plan || null;
  const problemLines = rowProblemLines(tr.rowStatus);

  const mgGroups = groupMgRuns(tr.mattergen?.runs || []);

  return (
    <SectionCard
      title="Results"
      actions={(onViewPlan || onPreviewSelection) ? (
        <div className="d-flex gap-2">
          {onViewPlan && (<button className="btn btn-outline-light btn-sm" onClick={onViewPlan}>View plan JSON</button>)}
          {onPreviewSelection && (<button className="btn btn-outline-info btn-sm" onClick={onPreviewSelection}>Preview selection</button>)}
        </div>
      ) : null}
    >
      {problemLines.length > 0 && (
        <div className="mb-2">
          {problemLines.map((line) => (
            <div key={line.rowId} data-row-status={line.status} style={{ fontSize: 13, color: line.status === 'rejected' ? '#f59e0b' : '#f87171' }}>{line.text}</div>
          ))}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-dark table-striped table-hover align-middle mb-0 text-light">
          <thead>
            <tr>
              <th>Service</th>
              <th>Variant</th>
              <th>Run</th>
              <th>Files</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mgGroups.map((g) => (
              <tr key={`mg-${g.key}`}>
                <td>MatterGen</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{g.label}</div>
                  {g.properties && Object.keys(g.properties).length > 0 && (
                    <div className="d-flex gap-1 flex-wrap mt-1">
                      {formatProps(g.properties).map((tag, i) => (
                        <span key={i} style={{ fontSize: 11, background: '#334155', padding: '2px 6px', borderRadius: 4, color: '#94a3b8' }}>{tag}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, marginTop: 2, color: '#94a3b8' }}>{g.totalCandidates} candidates</div>
                  <CappedNote runs={g.runs} />
                </td>
                <td>
                  {g.runIds.length === 1 ? g.runIds[0] : (
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{g.runIds.length} runs</span>
                  )}
                </td>
                <td>
                  <div className="d-flex gap-2 flex-wrap">
                    {g.extxyzUrl && (
                      <button className="btn btn-outline-info btn-sm" onClick={() => {
                        const meta = {
                          cifs: g.cifs.map(p => abs(g.base || mg, p)),
                          trajectories: g.trajectories.map(p => abs(g.base || mg, p)),
                          trajUrl: g.trajUrl ? abs(g.base || mg, g.trajUrl) : null,
                          serviceLabel: 'MatterGen',
                          modelLabel: VARIANT_LABELS[g.name] || g.name || 'Unknown',
                          runTs,
                          // Pooling gate (spec 2026-10-07 Part 0.5): where this variant ran.
                          // No fallback to `mg`: a row with no recorded base (legacy, or
                          // rebuilt from stored candidates) is never pooled.
                          dispatchBase: g.base || null,
                        };
                        // Spec 0.7: every job's extxyz with that job's own files, so the
                        // viewer merges them in job order and re-indexes the file lists.
                        const jobs = g.jobs.map((j) => ({
                          url: abs(g.base || mg, j.extxyz),
                          runId: j.runId,
                          candidates: j.candidates,
                          cifs: j.cifs.map(p => abs(g.base || mg, p)),
                          trajectories: j.trajectories.map(p => abs(g.base || mg, p)),
                        }));
                        if (ctx.openCrystals) {
                          ctx.openCrystals({ title: 'Crystals', url: abs(g.base || mg, g.extxyzUrl), meta, mgRunIds: g.runIds, jobs });
                        } else if (onOpenFile) {
                          onOpenFile({ title: 'Crystals (extxyz)', url: abs(g.base || mg, g.extxyzUrl), meta });
                        }
                      }}>View Crystals</button>
                    )}
                  </div>
                </td>
                <td>
                  {handleRefineMG && (
                    <button className="btn btn-outline-light btn-sm" onClick={() => handleRefineMG(g.runs[0], { totalCandidates: g.variantTotal || g.totalCandidates, chunkSize: g.chunkSize })}>Refine</button>
                  )}
                </td>
              </tr>
            ))}
            {(tr.fm4m?.runs || []).map((r, idx) => (
              <tr key={r.rowId ? `fm-${r.rowId}-${idx}` : `fm-${idx}`}>
                <td>FM4M</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{r.response?.model || r.request?.model || 'fm4m'}</div>
                  <FallbackNote run={r} />
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{Array.isArray(pl?.generation_spec?.system?.family) ? (pl.generation_spec.system.family.includes('polymer') ? 'polymer' : 'small_molecule') : 'molecular'}</div>
                  <CappedNote runs={[r]} />
                </td>
                <td>{r.response?.run_id || '-'}</td>
                <td>
                  <div className="d-flex gap-2 flex-wrap">
                    {Array.isArray(r.request?.inputs) && r.request.inputs.length > 0 && (
                      <button className="btn btn-outline-info btn-sm" onClick={() => onOpenFile && onOpenFile({
                        title: 'Molecules (FM4M)',
                        // B8: the file lives where this chunk ran (a relay or a custom model), not on the context FM4M base.
                        url: r.response?.files?.features_csv ? abs(r.base || ibm, r.response.files.features_csv) : null,
                        meta: {
                          molecules: true,
                          smiles: r.request.inputs,
                          features: Array.isArray(r.response?.features) ? r.response.features : null,
                          featureNames: Array.isArray(r.response?.feature_names) ? r.response.feature_names : null,
                          featureModel: r.response?.model || r.request?.model || null,
                          canonicalSmiles: Array.isArray(r.response?.canonical_smiles) ? r.response.canonical_smiles : null,
                          service: 'fm4m',
                          serviceRunId: r.response?.run_id || null,
                          dispatchBase: r.base || null, // pooling gate (spec 2026-10-07 Part 0.5); no context fallback
                        },
                      })}>View Molecules</button>
                    )}
                    {r.response?.files?.features_csv && (
                      <a className="btn btn-outline-secondary btn-sm" href={abs(r.base || ibm, r.response.files.features_csv)} target="_blank" rel="noreferrer">download features_csv</a>
                    )}
                  </div>
                </td>
                <td>
                  {handleRefineFM && (
                    <button className="btn btn-outline-light btn-sm" onClick={() => handleRefineFM(r)}>Refine</button>
                  )}
                </td>
              </tr>
            ))}
            {(tr.investigate?.runs || []).map((r, idx) => {
              const candidates = tr.investigate?.candidates || [];
              const formula = r?.request?.formula || candidates[0]?.formula || 'Unknown';
              const hasCif = r?.request?.has_cif || candidates.some(c => c.cif);
              const lookupSrc = r?.request?.lookup_source || candidates[0]?.source?.lookup_source;
              // CRI-8: when candidates were stripped before Firestore storage, synthesize a
              // minimal formula-only placeholder so the row still renders.
              // M11: never fabricate a truthy non-string `cif` — the boolean `true` flowed
              // into cifCache and the 3D viewer (addModel(true,'cif') → effect throw →
              // root ErrorBoundary wiped the app) and would have been POSTed to analyses.
              const isStripped = candidates.length === 0;
              const effectiveCandidates = isStripped && formula !== 'Unknown'
                ? [{ formula, cif: null, source: { lookup_source: lookupSrc } }]
                : candidates;
              return (
                <tr key={`inv-${idx}`}>
                  <td>Investigate</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{formula}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {hasCif
                        ? lookupSrc ? `Structure from ${lookupSrc}` : 'User-provided CIF'
                        : 'Formula only'}
                    </div>
                  </td>
                  <td>{r?.response?.run_id || '-'}</td>
                  <td>
                    <button className="btn btn-outline-info btn-sm" onClick={() => {
                      if (ctx.openCrystals) {
                        ctx.openCrystals({
                          title: `Investigate: ${formula}`,
                          url: null,
                          meta: { dbCandidates: effectiveCandidates },
                          mgRunIds: [],
                        });
                      }
                    }}>View Crystal</button>
                  </td>
                  <td />
                </tr>
              );
            })}
            {(tr.gt4sd?.runs || []).map((r, idx) => {
              const req = r?.request || {};
              const res = r?.response || {};
              const smilesList = Array.isArray(res.results) ? res.results : [];
              const canonSmiles = Array.isArray(res.canonical_smiles) ? res.canonical_smiles : smilesList;
              // GT4SD RT (Part D): `properties` is index-parallel to `results`, one
              // measurement per molecule. The viewer list drops falsy SMILES, so the
              // measurements are filtered by the SAME indices: filtering them
              // independently would shift every later molecule onto a neighbour's logP,
              // silently and with the counts still matching.
              const rtProperties = Array.isArray(res.properties) ? res.properties : null;
              const keptIdx = canonSmiles.map((smiles, i) => (smiles ? i : -1)).filter((i) => i >= 0);
              const viewerSmiles = keptIdx.map((i) => canonSmiles[i]);
              const viewerMeasurements = rtProperties ? keptIdx.map((i) => rtProperties[i] ?? null) : null;
              const hits = typeof res.hits_within_tolerance === 'number' ? res.hits_within_tolerance : null;
              return (
                <tr key={`gt4sd-${idx}`}>
                  <td>GT4SD</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{res.algorithm || req.algorithm || 'gt4sd'}</div>
                    {req.algorithm_version && <div style={{ fontSize: 11, color: '#94a3b8' }}>{req.algorithm_version}</div>}
                    {req.target_property && (
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {req.target_property}: {req.target_value ?? '—'}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{smilesList.length} molecules</div>
                    {hits !== null && (
                      <div style={{ fontSize: 11, color: hits > 0 ? '#4ade80' : '#f59e0b' }}>
                        {hits} of {smilesList.length} within{typeof res.tolerance_logp === 'number' ? ` ${res.tolerance_logp}` : ''} logP of the target
                      </div>
                    )}
                    <CappedNote runs={[r]} />
                  </td>
                  <td>{res.run_id || '-'}</td>
                  <td>
                    {smilesList.length > 0 && (
                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => onOpenFile && onOpenFile({
                          title: `Molecules (GT4SD · ${res.algorithm || 'gt4sd'})`,
                          url: null,
                          meta: {
                            molecules: true,
                            smiles: viewerSmiles,
                            service: 'gt4sd',
                            serviceRunId: res.run_id || null,
                            dispatchBase: r.base || null, // pooling gate (spec 2026-10-07 Part 0.5); no context fallback
                            // GT4SD RT: per-molecule measurements, same length and order
                            // as `smiles` above, or absent when the run measured nothing.
                            ...(viewerMeasurements ? { measurements: viewerMeasurements } : {}),
                            ...(typeof req.target_value === 'number' ? { targetValue: req.target_value } : {}),
                          },
                        })}
                      >
                        View Molecules
                      </button>
                    )}
                  </td>
                  <td />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export default ResultsTable;
export { groupMgRuns };
